<template lang="html">
  <div
    v-if="groups.length > 0"
    class="demographics-card"
    :class="{ 'demographics-card--compact': compact }"
  >
    <div v-for="group in groups" :key="group.metric_type" class="demographics-group">
      <div class="demographics-group-title">{{ group.label }}</div>

      <template v-if="isGenderGroup(group)">
        <pie-chart
          v-if="!compact"
          :data="pieData(group)"
          :colors="paletteFor(group)"
          :library="pieLibraryOptions"
          :download="false"
          height="160px"
        />
        <div class="demographics-legend">
          <div
            v-for="(row, index) in rowsFor(group)"
            :key="row.label"
            class="demographics-legend-item"
          >
            <span
              class="demographics-swatch"
              :style="{ backgroundColor: paletteFor(group)[index] }"
            />
            <span class="demographics-legend-label">{{ row.label }}</span>
            <span class="demographics-legend-value">{{ formatPercent(row.value) }}</span>
          </div>
        </div>
      </template>

      <div v-else class="demographics-bars">
        <div v-for="row in rowsFor(group)" :key="row.label" class="demographics-bar-row">
          <div class="demographics-bar-header">
            <span class="demographics-bar-label">
              <span v-if="flagFor(group, row.label)" class="demographics-flag">{{ flagFor(group, row.label) }}</span>
              {{ row.label }}
            </span>
            <span class="demographics-bar-value">{{ formatPercent(row.value) }}</span>
          </div>
          <div class="demographics-bar-track">
            <div
              class="demographics-bar-fill"
              :style="{ width: Math.min(row.value, 100) + '%' }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { flagForCountry } from "utils/demographics";

// InfluenceKit brand palette (semantic-ui/influencekit/globals/site.variables)
// — used instead of a neutral grayscale so this card reads as InfluenceKit,
// not a generic mockup.
const BRAND_CATEGORICAL_COLORS = [
  "#F4806F", // influenceKitPink (primary)
  "#2D5264", // influenceKitBlue
  "#A1BCB1", // influenceKitMint
  "#E5B586", // influenceKitGold
  "#A5A7A9", // influenceKitGray
];
export default {
  props: {
    demographics: {
      type: Array,
      default: function () {
        return [];
      },
    },
    // Renders without each group's own card chrome (background/shadow/
    // padding) — used inside the deliverable card's hover popup, which
    // already has its own popup chrome, so a card-within-a-card would be
    // redundant.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    groups() {
      return this.demographics || [];
    },
    pieLibraryOptions() {
      return {
        plugins: {
          legend: {display: false},
        },
      };
    },
  },
  methods: {
    isGenderGroup(group) {
      return group.metric_type === "demographics_gender";
    },
    rowsFor(group) {
      return Object.keys(group.values || {}).map((label) => ({
        label,
        value: Number(group.values[label]) || 0,
      }));
    },
    pieData(group) {
      return this.rowsFor(group).map((row) => [row.label, row.value]);
    },
    paletteFor(group) {
      return this.rowsFor(group).map(
        (_row, index) => BRAND_CATEGORICAL_COLORS[index % BRAND_CATEGORICAL_COLORS.length]
      );
    },
    flagFor(group, label) {
      if (group.metric_type !== "demographics_geo_country") return null;
      return flagForCountry(label);
    },
    formatPercent(value) {
      return `${Number(value).toFixed(1)}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.demographics-card {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

// Wraps by the card's own rendered width (flex-wrap), not the viewport —
// this card renders both full-width (report) and narrow (the "Preview & edit"
// panel is a fixed ~380px column regardless of viewport size), and Fomantic's
// `stackable` grid only responds to a viewport media query, not a container's
// actual width, so it stayed 3-across and collapsed into unreadable
// one-letter-per-line text in the narrow preview.
.demographics-group {
  flex: 1 1 160px;
  min-width: 160px;
  background: var(--bg-surface, #fff);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px var(--border-light, rgba(0, 0, 0, 0.06));
  padding: 1rem;
}

.demographics-group-title {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--text-muted, #787a92);
  margin-bottom: 0.9rem;
}

.demographics-card--compact {
  gap: 0;
  flex-direction: column;
  flex-wrap: nowrap;

  .demographics-group {
    flex: none;
    min-width: 0;
    background: transparent;
    box-shadow: none;
    padding: 0.6rem 0;
    border-top: 1px solid var(--border-light, #eef0f2);

    &:first-child {
      padding-top: 0;
      border-top: none;
    }
  }

  .demographics-group-title {
    margin-bottom: 0.5rem;
  }
}

.demographics-legend {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.demographics-legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.demographics-swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.demographics-legend-label {
  font-weight: 700;
  color: var(--text-primary, #1a202c);
  flex: 1;
}

.demographics-legend-value {
  font-weight: 700;
  color: var(--text-primary, #1a202c);
}

.demographics-bars {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.demographics-bar-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.85rem;
  margin-bottom: 0.3rem;
}

.demographics-bar-label {
  color: var(--text-primary, #1a202c);
}

.demographics-flag {
  margin-right: 0.3rem;
}

.demographics-bar-value {
  font-weight: 700;
  color: var(--text-primary, #1a202c);
}

.demographics-bar-track {
  background: var(--border-light, #edf2f7);
  border-radius: 3px;
  height: 8px;
  overflow: hidden;
}

.demographics-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: #f4806f;
  transition: width 0.4s ease;
}
</style>
